import { useMemo, useState } from "react";

import { getUserFacingMessage, reportError } from "../../../api/errors";
import {
  analyzeMemberPointsImport,
  commitMemberPointsImport,
  downloadMemberPointsImportTemplate,
  type MemberPointsImportAnalyzeRow,
  type MemberPointsImportCategoryColumn,
  type MemberPointsImportCommitResult,
  type MemberPointsImportDetailRow,
} from "../../../api/memberPointsImport";
import { useAdminData } from "../AdminDataContext";

type UiRow = MemberPointsImportAnalyzeRow & { selected: boolean };

type OpBarState = {
  phase: "upload" | "processing";
  uploadPct: number;
};

function ProgressPanel({
  title,
  state,
  uploadHint,
  processingHint,
}: {
  title: string;
  state: OpBarState;
  uploadHint: string;
  processingHint: string;
}) {
  const isUpload = state.phase === "upload";
  const barPct = isUpload ? state.uploadPct : 0;
  return (
    <div className="rounded-xl border border-red-100 bg-red-50/60 px-4 py-3 space-y-2">
      <div className="flex items-center justify-between gap-2 text-xs text-gray-700">
        <span className="font-medium text-gray-800">{title}</span>
        {isUpload ? <span className="tabular-nums text-gray-600">{state.uploadPct}%</span> : null}
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200/90">
        {isUpload ? (
          <div
            className="h-full rounded-full bg-red-500 transition-[width] duration-150 ease-out"
            style={{ width: `${Math.min(100, Math.max(0, barPct))}%` }}
          />
        ) : (
          <div className="h-full w-full rounded-full bg-red-400/80 animate-pulse" />
        )}
      </div>
      <p className="text-xs text-gray-600 flex items-center gap-2">
        <i className="ri-loader-4-line animate-spin shrink-0" />
        {isUpload ? uploadHint : processingHint}
      </p>
    </div>
  );
}

export default function MemberPointsImport() {
  const { tenantLegacyId, reload } = useAdminData();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<null | "analyze" | "commit" | "template">(null);
  const [analyzeBar, setAnalyzeBar] = useState<OpBarState | null>(null);
  const [commitBar, setCommitBar] = useState<OpBarState | null>(null);
  const [uiRows, setUiRows] = useState<UiRow[]>([]);
  const [categoryColumns, setCategoryColumns] = useState<MemberPointsImportCategoryColumn[]>([]);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<MemberPointsImportCommitResult | null>(null);
  const [importDetails, setImportDetails] = useState<MemberPointsImportDetailRow[]>([]);

  const selectedCount = useMemo(() => uiRows.filter((r) => r.selected).length, [uiRows]);
  const recommended = useMemo(() => {
    const headers = ["idNumber", "points", "memo", "activityLegacyId"];
    for (const c of categoryColumns) headers.push(c.header || c.name);
    return headers.join(", ");
  }, [categoryColumns]);

  const resetSession = () => {
    setFile(null);
    setUiRows([]);
    setCategoryColumns([]);
    setAnalyzeError(null);
    setCommitResult(null);
    setImportDetails([]);
    setAnalyzeBar(null);
    setCommitBar(null);
  };

  const onPickFile = async (f: File | null) => {
    setFile(f);
    setUiRows([]);
    setCategoryColumns([]);
    setAnalyzeError(null);
    setCommitResult(null);
    setImportDetails([]);
    if (!f) return;
    setBusy("analyze");
    setAnalyzeBar({ phase: "upload", uploadPct: 0 });
    try {
      const res = await analyzeMemberPointsImport(tenantLegacyId, f, {
        onUploadProgress: (loaded, total) => {
          if (total > 0) setAnalyzeBar({ phase: "upload", uploadPct: Math.round((100 * loaded) / total) });
        },
        onUploadFinished: () => setAnalyzeBar({ phase: "processing", uploadPct: 100 }),
      });
      setCategoryColumns(res.categoryColumns ?? []);
      setUiRows((res.rows ?? []).map((r) => ({ ...r, selected: r.defaultSelected })));
    } catch (e) {
      setAnalyzeError(getUserFacingMessage(e, "解析失败"));
      setUiRows([]);
    } finally {
      setBusy(null);
      setAnalyzeBar(null);
    }
  };

  const applyDefaultSelection = () => {
    setUiRows((prev) => prev.map((r) => ({ ...r, selected: r.defaultSelected })));
  };

  const clearSelection = () => {
    setUiRows((prev) => prev.map((r) => ({ ...r, selected: false })));
  };

  const selectAllValid = () => {
    setUiRows((prev) =>
      prev.map((r) => ({
        ...r,
        selected: !r.remark && !!r.memberLegacyId && r.points > 0,
      })),
    );
  };

  const doCommit = async () => {
    const selected = uiRows.filter((r) => r.selected);
    if (selected.length === 0) return;
    const missingMember = selected.find((r) => !r.memberLegacyId);
    if (missingMember) {
      setAnalyzeError(`第 ${missingMember.row} 行未匹配到会员，请取消勾选或修正后重试`);
      return;
    }

    setBusy("commit");
    setCommitResult(null);
    setImportDetails([]);
    setCommitBar({ phase: "upload", uploadPct: 0 });
    try {
      const res = await commitMemberPointsImport(
        tenantLegacyId,
        selected.map((r) => ({
          row: r.row,
          memberLegacyId: r.memberLegacyId!,
          points: r.points,
          memo: r.memo,
          activityLegacyId: r.activityLegacyId || undefined,
          categoryParticipations: r.categoryParticipations ?? [],
        })),
        {
          onUploadProgress: (loaded, total) => {
            if (total > 0) setCommitBar({ phase: "upload", uploadPct: Math.round((100 * loaded) / total) });
          },
          onUploadFinished: () => setCommitBar({ phase: "processing", uploadPct: 100 }),
        },
      );
      setCommitResult(res);
      if (res.ok) {
        setImportDetails(res.details);
        await reload();
      } else if (res.errors?.length) {
        setAnalyzeError(res.errors.map((e) => `第 ${e.row} 行：${e.message}`).join("；"));
      }
    } catch (e) {
      setAnalyzeError(getUserFacingMessage(e, "导入失败"));
    } finally {
      setBusy(null);
      setCommitBar(null);
    }
  };

  const onDownloadTemplate = async () => {
    setBusy("template");
    try {
      const blob = await downloadMemberPointsImportTemplate(tenantLegacyId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "member-points-import-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      reportError(e, "下载模板失败");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-900">导入会员积分</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                通过 Excel（.xlsx）或 CSV 按<strong>身份证号</strong>匹配会员，批量补录积分，并填写各启用活动类型的参加次数。
                <strong>积分</strong>仍必填（正整数，照常入账）。类型列填整数次数，允许 0、空单元格不计入；同一会员同一类型再次导入会累加。停用类型不会出现在模板中。
              </p>
            </div>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void onDownloadTemplate()}
              className="text-xs text-gray-500 hover:text-red-600 transition-colors disabled:opacity-40"
            >
              <i className="ri-download-line mr-1" />
              下载 Excel 模板
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <i className="ri-file-excel-2-line text-green-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file ? file.name : "请选择 Excel（.xlsx）或 CSV"}
                  </p>
                  <p className="text-xs text-gray-500">
                    表头须含「身份证号」「积分」；其余列为当前启用活动类型名称（填参加次数；空=不计入，0 合法）。
                    {categoryColumns.length > 0 ? (
                      <>
                        {" "}当前类型列：
                        <span className="font-mono">{recommended}</span>
                      </>
                    ) : null}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <label className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:border-red-300 hover:text-red-600 cursor-pointer transition-colors">
                  <i className="ri-upload-2-line" />
                  选择文件
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                <button
                  type="button"
                  disabled={uiRows.length === 0 || busy !== null}
                  onClick={applyDefaultSelection}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-white disabled:opacity-40"
                >
                  按预检默认勾选
                </button>
                <button
                  type="button"
                  disabled={uiRows.length === 0 || busy !== null}
                  onClick={selectAllValid}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-white disabled:opacity-40"
                >
                  勾选全部有效行
                </button>
                <button
                  type="button"
                  disabled={uiRows.length === 0 || busy !== null}
                  onClick={clearSelection}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-white disabled:opacity-40"
                >
                  清空勾选
                </button>
                <button
                  type="button"
                  disabled={selectedCount === 0 || busy !== null}
                  onClick={() => void doCommit()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-40"
                >
                  <i className="ri-play-circle-line" />
                  {busy === "commit" ? "导入中…" : `确认导入（${selectedCount}）`}
                </button>
                {(uiRows.length > 0 || commitResult) && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={resetSession}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-gray-500 hover:text-gray-800 disabled:opacity-40"
                  >
                    重置
                  </button>
                )}
              </div>
            </div>
          </div>

          {busy === "analyze" && analyzeBar ? (
            <ProgressPanel
              title="文件预检"
              state={analyzeBar}
              uploadHint="正在上传文件到服务器…"
              processingHint="正在解析并匹配会员，请稍候…"
            />
          ) : null}

          {busy === "commit" && commitBar ? (
            <ProgressPanel
              title="批量写入积分"
              state={commitBar}
              uploadHint="正在提交导入数据…"
              processingHint="正在写入积分流水，请稍候…"
            />
          ) : null}

          {analyzeError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{analyzeError}</div>
          )}

          {commitResult?.ok && (
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
              成功导入 <strong>{commitResult.succeeded}</strong> 条，共发放{" "}
              <strong>{commitResult.totalPoints}</strong> 积分。
            </div>
          )}

          {uiRows.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-800">预检列表</p>
                <p className="text-xs text-gray-500">
                  已勾选 {selectedCount} / {uiRows.length}
                </p>
              </div>
              <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="text-left font-medium px-2 py-2 w-10" />
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">行</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">身份证号</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">匹配会员</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">积分</th>
                      {categoryColumns.map((c) => (
                        <th
                          key={c.categoryKey}
                          className="text-left font-medium px-2 py-2 whitespace-nowrap"
                          title={c.categoryKey}
                        >
                          {c.header || c.name}
                        </th>
                      ))}
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">当前余额</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">活动</th>
                      <th className="text-left font-medium px-2 py-2 min-w-[160px]">备注/说明</th>
                      <th className="text-left font-medium px-2 py-2 min-w-[180px]">预检</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {uiRows.map((r, idx) => (
                      <tr key={r.row} className={r.selected ? "bg-white" : "bg-gray-50/60"}>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={r.selected}
                            disabled={busy !== null}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setUiRows((prev) =>
                                prev.map((x, j) => (j === idx ? { ...x, selected: checked } : x)),
                              );
                            }}
                          />
                        </td>
                        <td className="px-2 py-2 text-gray-500">{r.row}</td>
                        <td className="px-2 py-2 font-mono text-gray-900">{r.idNumber || "—"}</td>
                        <td className="px-2 py-2">
                          {r.matchedMemberName || "—"}
                          {r.matchedMemberNo ? (
                            <span className="ml-1 font-mono text-gray-400">{r.matchedMemberNo}</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 font-semibold text-teal-700">+{r.points}</td>
                        {categoryColumns.map((c) => {
                          const hit = (r.categoryParticipations ?? []).find(
                            (p) => p.categoryKey === c.categoryKey,
                          );
                          return (
                            <td key={c.categoryKey} className="px-2 py-2 tabular-nums">
                              {hit ? hit.count : "—"}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 tabular-nums">{r.currentPoints ?? "—"}</td>
                        <td className="px-2 py-2">
                          {r.activityTitle || r.activityLegacyId || "—"}
                        </td>
                        <td className="px-2 py-2 text-gray-700">{r.memo || "—"}</td>
                        <td className="px-2 py-2 text-amber-900">{r.remark || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {importDetails.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
                <p className="text-sm font-semibold text-gray-800">导入结果明细</p>
              </div>
              <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="text-left font-medium px-2 py-2">行</th>
                      <th className="text-left font-medium px-2 py-2">会员号</th>
                      <th className="text-left font-medium px-2 py-2">姓名</th>
                      <th className="text-left font-medium px-2 py-2">积分</th>
                      <th className="text-left font-medium px-2 py-2">类型次数</th>
                      <th className="text-left font-medium px-2 py-2">余额变化</th>
                      <th className="text-left font-medium px-2 py-2">说明</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {importDetails.map((d) => (
                      <tr key={`${d.row}-${d.memberLegacyId}`}>
                        <td className="px-2 py-2 text-gray-500">{d.row}</td>
                        <td className="px-2 py-2 font-mono">{d.memberNo}</td>
                        <td className="px-2 py-2">{d.memberName}</td>
                        <td className="px-2 py-2 font-semibold text-teal-700">+{d.points}</td>
                        <td className="px-2 py-2 text-gray-700">
                          {(d.categoryParticipations ?? []).length === 0
                            ? "—"
                            : (d.categoryParticipations ?? [])
                                .map((p) => `${p.categoryName} +${p.added ?? p.count}${p.total != null ? `（合计 ${p.total}）` : ""}`)
                                .join("、")}
                        </td>
                        <td className="px-2 py-2 tabular-nums">
                          {d.balanceBefore} → {d.balanceAfter}
                        </td>
                        <td className="px-2 py-2 text-gray-700">{d.memo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
